export type NavCrumb = {
  labelKey?: string
  fallback: string
  path?: string
  /** Resolved dynamic label (entity / family / control name). Wins over i18n. */
  label?: string
}

export type NavCrumbContext = {
  norm?: string
  familyId?: string
  familyLabel?: string
  controlLabel?: string
  missionId?: string
  missionLabel?: string
  pipelineId?: string
  pipelineLabel?: string
  projectId?: string
  projectLabel?: string
  taskId?: string
  taskLabel?: string
  auditId?: string
  auditLabel?: string
  incidentId?: string
  incidentLabel?: string
  riskId?: string
  riskLabel?: string
  planId?: string
  planLabel?: string
}

type CrumbRule = {
  test: RegExp
  crumbs: NavCrumb[] | ((ctx: NavCrumbContext, pathname: string) => NavCrumb[])
}

const home: NavCrumb = { labelKey: 'home', fallback: 'Home', path: '/' }

function missionCrumb(ctx: NavCrumbContext): NavCrumb {
  return {
    labelKey: 'nav.mission',
    fallback: 'Mission',
    label: ctx.missionLabel || undefined,
    path: ctx.missionId ? `/mission-details/${ctx.missionId}` : undefined,
  }
}

/**
 * Topbar crumbs — full path, parents clickable so users can always go back.
 * More specific routes must come before broader ones.
 */
const RULES: CrumbRule[] = [
  /* ---- Maturity ---- */
  {
    test: /^\/subDomain-Maturityscoring\//,
    crumbs: [
      home,
      {
        labelKey: 'maturityManagement',
        fallback: 'Maturity Management',
        path: '/maturite',
      },
      { labelKey: 'nav.analysis', fallback: 'Analysis' },
    ],
  },
  {
    test: /^\/maturity\/view-analysis/,
    crumbs: [
      home,
      {
        labelKey: 'maturityManagement',
        fallback: 'Maturity Management',
        path: '/maturite',
      },
      { labelKey: 'nav.analysis', fallback: 'Analysis' },
    ],
  },
  {
    test: /^\/maturite$|^\/maturity\//,
    crumbs: [
      home,
      { labelKey: 'maturityManagement', fallback: 'Maturity Management' },
    ],
  },

  /* ---- Mission nested: analysis / update / sections ---- */
  {
    test: /^\/mission\/view-analysis\//,
    crumbs: (ctx) => [
      home,
      { labelKey: 'missions', fallback: 'Missions', path: '/missions' },
      missionCrumb(ctx),
      { labelKey: 'nav.analysis', fallback: 'Analysis' },
    ],
  },
  {
    test: /^\/mission-priority\//,
    crumbs: (ctx) => [
      home,
      { labelKey: 'missions', fallback: 'Missions', path: '/missions' },
      missionCrumb(ctx),
      { labelKey: 'nav.priority', fallback: 'Priority' },
    ],
  },
  {
    test: /^\/mission-details\/[^/]+\/pipelines/,
    crumbs: (ctx) => [
      home,
      { labelKey: 'missions', fallback: 'Missions', path: '/missions' },
      missionCrumb(ctx),
      { labelKey: 'pipelines', fallback: 'Pipelines' },
    ],
  },
  {
    test: /^\/mission-details\/[^/]+\/projects/,
    crumbs: (ctx) => [
      home,
      { labelKey: 'missions', fallback: 'Missions', path: '/missions' },
      missionCrumb(ctx),
      { labelKey: 'projects', fallback: 'Projects' },
    ],
  },
  {
    test: /^\/mission-details\/[^/]+\/tasks/,
    crumbs: (ctx) => [
      home,
      { labelKey: 'missions', fallback: 'Missions', path: '/missions' },
      missionCrumb(ctx),
      { labelKey: 'tasks', fallback: 'Tasks' },
    ],
  },
  {
    test: /^\/mission-details\/[^/]+\/risk-treatment-plans/,
    crumbs: (ctx) => [
      home,
      { labelKey: 'missions', fallback: 'Missions', path: '/missions' },
      missionCrumb(ctx),
      { labelKey: 'treatmentPlan', fallback: 'Risk Treatment Plans' },
    ],
  },
  {
    test: /^\/mission-details\//,
    crumbs: (ctx) => [
      home,
      { labelKey: 'missions', fallback: 'Missions', path: '/missions' },
      {
        labelKey: 'nav.mission',
        fallback: 'Mission',
        label: ctx.missionLabel || undefined,
      },
    ],
  },
  {
    test: /^\/addmission$|^\/mission-create$|^\/missions\/new/,
    crumbs: [
      home,
      { labelKey: 'missions', fallback: 'Missions', path: '/missions' },
      { labelKey: 'nav.addMission', fallback: 'Add a new mission' },
    ],
  },
  {
    test: /^\/mission\/[^/]+\/pipeline\/[^/]+\/addprojet/,
    crumbs: (ctx) => [
      home,
      { labelKey: 'missions', fallback: 'Missions', path: '/missions' },
      missionCrumb(ctx),
      {
        labelKey: 'pipelines',
        fallback: 'Pipelines',
        path: ctx.pipelineId
          ? `/mission/${ctx.missionId}/pipeline/${ctx.pipelineId}`
          : '/pipelines',
        label: ctx.pipelineLabel || undefined,
      },
      { labelKey: 'nav.addProject', fallback: 'Add Project' },
    ],
  },
  {
    test: /^\/mission\/[^/]+\/pipeline\//,
    crumbs: (ctx) => [
      home,
      { labelKey: 'missions', fallback: 'Missions', path: '/missions' },
      missionCrumb(ctx),
      {
        labelKey: 'pipelines',
        fallback: 'Pipelines',
        path: ctx.missionId
          ? `/mission-details/${ctx.missionId}`
          : '/pipelines',
      },
      {
        labelKey: 'nav.pipeline',
        fallback: 'Pipeline',
        label: ctx.pipelineLabel || undefined,
      },
    ],
  },
  {
    test: /^\/mission\/[^/]+\/projet\/[^/]+\/addaction/,
    crumbs: (ctx) => [
      home,
      { labelKey: 'missions', fallback: 'Missions', path: '/missions' },
      missionCrumb(ctx),
      {
        labelKey: 'projects',
        fallback: 'Projects',
        path:
          ctx.missionId && ctx.projectId
            ? `/mission/${ctx.missionId}/projet/${ctx.projectId}`
            : '/projets',
        label: ctx.projectLabel || undefined,
      },
      { labelKey: 'nav.addTask', fallback: 'Add Task' },
    ],
  },
  {
    test: /^\/mission\/[^/]+\/projet\//,
    crumbs: (ctx) => [
      home,
      { labelKey: 'missions', fallback: 'Missions', path: '/missions' },
      missionCrumb(ctx),
      {
        labelKey: 'projects',
        fallback: 'Projects',
        path: ctx.missionId
          ? `/mission-details/${ctx.missionId}`
          : '/projets',
      },
      {
        labelKey: 'nav.project',
        fallback: 'Project',
        label: ctx.projectLabel || undefined,
      },
    ],
  },
  {
    test: /^\/mission\/[^/]+\/addprojet/,
    crumbs: (ctx) => [
      home,
      { labelKey: 'missions', fallback: 'Missions', path: '/missions' },
      missionCrumb(ctx),
      { labelKey: 'nav.addProject', fallback: 'Add Project' },
    ],
  },
  {
    test: /^\/mission\/[^/]+\/addaction/,
    crumbs: (ctx) => [
      home,
      { labelKey: 'missions', fallback: 'Missions', path: '/missions' },
      missionCrumb(ctx),
      { labelKey: 'nav.addTask', fallback: 'Add Task' },
    ],
  },
  {
    test: /^\/mission\/[^/]+\/addplan/,
    crumbs: (ctx) => [
      home,
      { labelKey: 'missions', fallback: 'Missions', path: '/missions' },
      missionCrumb(ctx),
      { labelKey: 'nav.addPlan', fallback: 'Add Treatment Plan' },
    ],
  },
  {
    test: /^\/mission\/[^/]+$/,
    crumbs: (ctx) => [
      home,
      { labelKey: 'missions', fallback: 'Missions', path: '/missions' },
      missionCrumb(ctx),
      { labelKey: 'nav.updateMission', fallback: 'Update Mission' },
    ],
  },
  {
    test: /^\/missions$/,
    crumbs: [
      home,
      { labelKey: 'missions', fallback: 'Missions' },
    ],
  },

  /* ---- Pipelines (global) ---- */
  {
    test: /^\/addpipeline$/,
    crumbs: [
      home,
      { labelKey: 'pipelines', fallback: 'Pipelines', path: '/pipelines' },
      { labelKey: 'nav.addPipeline', fallback: 'Add Pipeline' },
    ],
  },
  {
    test: /^\/pipeline\/[^/]+$/,
    crumbs: (ctx) => [
      home,
      { labelKey: 'pipelines', fallback: 'Pipelines', path: '/pipelines' },
      {
        labelKey: 'nav.pipeline',
        fallback: 'Pipeline',
        label: ctx.pipelineLabel || undefined,
      },
    ],
  },
  {
    test: /^\/pipelines$/,
    crumbs: [
      home,
      { labelKey: 'pipelines', fallback: 'Pipelines' },
    ],
  },

  /* ---- Projects (global) ---- */
  {
    test: /^\/addprojet$/,
    crumbs: [
      home,
      { labelKey: 'projects', fallback: 'Projects', path: '/projets' },
      { labelKey: 'nav.addProject', fallback: 'Add Project' },
    ],
  },
  {
    test: /^\/projet\/[^/]+$/,
    crumbs: (ctx) => [
      home,
      { labelKey: 'projects', fallback: 'Projects', path: '/projets' },
      {
        labelKey: 'nav.project',
        fallback: 'Project',
        label: ctx.projectLabel || undefined,
      },
    ],
  },
  {
    test: /^\/project\/view-analysis\//,
    crumbs: (ctx) => [
      home,
      { labelKey: 'projects', fallback: 'Projects', path: '/projets' },
      {
        labelKey: 'nav.project',
        fallback: 'Project',
        label: ctx.projectLabel || undefined,
        path:
          ctx.missionId && ctx.projectId
            ? `/mission/${ctx.missionId}/projet/${ctx.projectId}`
            : undefined,
      },
      { labelKey: 'nav.analysis', fallback: 'Analysis' },
    ],
  },
  {
    test: /^\/projets$/,
    crumbs: [
      home,
      { labelKey: 'projects', fallback: 'Projects' },
    ],
  },

  /* ---- Tasks ---- */
  {
    test: /^\/addaction$/,
    crumbs: [
      home,
      { labelKey: 'tasks', fallback: 'Tasks', path: '/actions' },
      { labelKey: 'nav.addTask', fallback: 'Add Task' },
    ],
  },
  {
    test: /^\/actiond\/|^\/action\/[^/]+$/,
    crumbs: (ctx) => [
      home,
      { labelKey: 'tasks', fallback: 'Tasks', path: '/actions' },
      {
        labelKey: 'nav.task',
        fallback: 'Task',
        label: ctx.taskLabel || undefined,
      },
    ],
  },
  {
    test: /^\/actions$/,
    crumbs: [
      home,
      { labelKey: 'tasks', fallback: 'Tasks' },
    ],
  },

  /* ---- Audits ---- */
  {
    test: /^\/addaudit$/,
    crumbs: [
      home,
      { labelKey: 'audits', fallback: 'Audits', path: '/audits' },
      { labelKey: 'nav.addAudit', fallback: 'Add Audit' },
    ],
  },
  {
    test: /^\/audit\/view-analysis\//,
    crumbs: (ctx) => [
      home,
      { labelKey: 'audits', fallback: 'Audits', path: '/audits' },
      {
        labelKey: 'nav.audit',
        fallback: 'Audit',
        label: ctx.auditLabel || undefined,
        path: ctx.auditId ? `/audit-detail/${ctx.auditId}` : undefined,
      },
      { labelKey: 'nav.analysis', fallback: 'Analysis' },
    ],
  },
  {
    test: /^\/audit-detail\//,
    crumbs: (ctx) => [
      home,
      { labelKey: 'audits', fallback: 'Audits', path: '/audits' },
      {
        labelKey: 'nav.audit',
        fallback: 'Audit',
        label: ctx.auditLabel || undefined,
      },
    ],
  },
  {
    test: /^\/audit\/[^/]+$/,
    crumbs: (ctx) => [
      home,
      { labelKey: 'audits', fallback: 'Audits', path: '/audits' },
      {
        labelKey: 'nav.audit',
        fallback: 'Audit',
        label: ctx.auditLabel || undefined,
        path: ctx.auditId ? `/audit-detail/${ctx.auditId}` : undefined,
      },
      { labelKey: 'nav.update', fallback: 'Update' },
    ],
  },
  {
    test: /^\/audits$/,
    crumbs: [
      home,
      { labelKey: 'audits', fallback: 'Audits' },
    ],
  },

  /* ---- Incidents ---- */
  {
    test: /^\/addincident$/,
    crumbs: [
      home,
      { labelKey: 'incidents', fallback: 'Incidents', path: '/incidents' },
      { labelKey: 'nav.addIncident', fallback: 'Add Incident' },
    ],
  },
  {
    test: /^\/incident\/view-analysis\//,
    crumbs: (ctx) => [
      home,
      { labelKey: 'incidents', fallback: 'Incidents', path: '/incidents' },
      {
        labelKey: 'nav.incident',
        fallback: 'Incident',
        label: ctx.incidentLabel || undefined,
        path: ctx.incidentId ? `/incident-detail/${ctx.incidentId}` : undefined,
      },
      { labelKey: 'nav.analysis', fallback: 'Analysis' },
    ],
  },
  {
    test: /^\/incident-detail\//,
    crumbs: (ctx) => [
      home,
      { labelKey: 'incidents', fallback: 'Incidents', path: '/incidents' },
      {
        labelKey: 'nav.incident',
        fallback: 'Incident',
        label: ctx.incidentLabel || undefined,
      },
    ],
  },
  {
    test: /^\/incident\/[^/]+$/,
    crumbs: (ctx) => [
      home,
      { labelKey: 'incidents', fallback: 'Incidents', path: '/incidents' },
      {
        labelKey: 'nav.incident',
        fallback: 'Incident',
        label: ctx.incidentLabel || undefined,
        path: ctx.incidentId ? `/incident-detail/${ctx.incidentId}` : undefined,
      },
      { labelKey: 'nav.update', fallback: 'Update' },
    ],
  },
  {
    test: /^\/incidents$/,
    crumbs: [
      home,
      { labelKey: 'incidents', fallback: 'Incidents' },
    ],
  },

  /* ---- Risks ---- */
  {
    test: /^\/addRisque$/,
    crumbs: [
      home,
      { labelKey: 'risks', fallback: 'Risks', path: '/risques' },
      { labelKey: 'nav.addRisk', fallback: 'Add Risk' },
    ],
  },
  {
    test: /^\/risque-detail\/view-analysis\//,
    crumbs: (ctx) => [
      home,
      { labelKey: 'risks', fallback: 'Risks', path: '/risques' },
      {
        labelKey: 'nav.risk',
        fallback: 'Risk',
        label: ctx.riskLabel || undefined,
        path: ctx.riskId ? `/risque-detail/${ctx.riskId}` : undefined,
      },
      { labelKey: 'nav.analysis', fallback: 'Analysis' },
    ],
  },
  {
    test: /^\/risque-detail\//,
    crumbs: (ctx) => [
      home,
      { labelKey: 'risks', fallback: 'Risks', path: '/risques' },
      {
        labelKey: 'nav.risk',
        fallback: 'Risk',
        label: ctx.riskLabel || undefined,
      },
    ],
  },
  {
    test: /^\/risque\/[^/]+$/,
    crumbs: (ctx) => [
      home,
      { labelKey: 'risks', fallback: 'Risks', path: '/risques' },
      {
        labelKey: 'nav.risk',
        fallback: 'Risk',
        label: ctx.riskLabel || undefined,
        path: ctx.riskId ? `/risque-detail/${ctx.riskId}` : undefined,
      },
      { labelKey: 'nav.update', fallback: 'Update' },
    ],
  },
  {
    test: /^\/risques$/,
    crumbs: [
      home,
      { labelKey: 'risks', fallback: 'Risks' },
    ],
  },

  /* ---- Plans ---- */
  {
    test: /^\/addplan$/,
    crumbs: [
      home,
      {
        labelKey: 'treatmentPlan',
        fallback: 'Risk Treatment Plan',
        path: '/plan',
      },
      { labelKey: 'nav.addPlan', fallback: 'Add Treatment' },
    ],
  },
  {
    test: /^\/riskTreatmentPlan\/view-analysis\//,
    crumbs: (ctx) => [
      home,
      {
        labelKey: 'treatmentPlan',
        fallback: 'Risk Treatment Plan',
        path: '/plan',
      },
      {
        labelKey: 'nav.plan',
        fallback: 'Plan',
        label: ctx.planLabel || undefined,
        path: ctx.planId ? `/plan-detail/${ctx.planId}` : undefined,
      },
      { labelKey: 'nav.analysis', fallback: 'Analysis' },
    ],
  },
  {
    test: /^\/plan-detail\//,
    crumbs: (ctx) => [
      home,
      {
        labelKey: 'treatmentPlan',
        fallback: 'Risk Treatment Plan',
        path: '/plan',
      },
      {
        labelKey: 'nav.plan',
        fallback: 'Plan',
        label: ctx.planLabel || undefined,
      },
    ],
  },
  {
    test: /^\/plan\/[^/]+$/,
    crumbs: (ctx) => [
      home,
      {
        labelKey: 'treatmentPlan',
        fallback: 'Risk Treatment Plan',
        path: '/plan',
      },
      {
        labelKey: 'nav.plan',
        fallback: 'Plan',
        label: ctx.planLabel || undefined,
        path: ctx.planId ? `/plan-detail/${ctx.planId}` : undefined,
      },
      { labelKey: 'nav.update', fallback: 'Update' },
    ],
  },
  {
    test: /^\/plan$/,
    crumbs: [
      home,
      { labelKey: 'treatmentPlan', fallback: 'Risk Treatment Plan' },
    ],
  },

  /* ---- Security ---- */
  {
    test: /^\/related-controls\/[^/]+\/view-analysis\//,
    crumbs: (ctx, pathname) => {
      const match = pathname.match(
        /^\/related-controls\/([^/]+)\/view-analysis\/([^/]+)/
      )
      const source = match?.[1]
      const controlId = match?.[2]
      const mapPath =
        source && controlId
          ? `/relatedDataMapped/${source}/${controlId}`
          : '/securityFramework'
      return [
        home,
        {
          labelKey: 'sidebar.securityFramework',
          fallback: 'Framework Dependencies',
          path: '/securityFramework',
        },
        {
          labelKey: 'frameworkMappingPage.steps.reviewMapping',
          fallback: 'Review mapping',
          path: mapPath,
          label: ctx.controlLabel || undefined,
        },
        { labelKey: 'nav.analysis', fallback: 'Analysis' },
      ]
    },
  },
  {
    test: /^\/security-control-details\/view-analysis\//,
    crumbs: (ctx, pathname) => {
      const controlId = pathname.match(
        /^\/security-control-details\/view-analysis\/([^/]+)/
      )?.[1]
      const normPath = ctx.norm ? `/security/${ctx.norm}` : undefined
      const familyPath = ctx.familyId
        ? `/security-controls/${ctx.familyId}`
        : undefined
      const detailPath = controlId
        ? `/security-control-details/${controlId}`
        : undefined
      return [
        home,
        {
          labelKey: 'securityControls',
          fallback: 'Security Controls',
          path: normPath,
        },
        {
          labelKey: 'nav.securityFamilyControls',
          fallback: 'Controls',
          label: ctx.familyLabel || undefined,
          path: familyPath,
        },
        {
          labelKey: 'nav.securityControlDetail',
          fallback: 'Control details',
          label: ctx.controlLabel || undefined,
          path: detailPath,
        },
        { labelKey: 'nav.analysis', fallback: 'Analysis' },
      ]
    },
  },
  {
    test: /^\/security-controls\/view-analysis\//,
    crumbs: (ctx, pathname) => {
      const familyId = pathname.match(
        /^\/security-controls\/view-analysis\/([^/]+)/
      )?.[1]
      const normPath = ctx.norm ? `/security/${ctx.norm}` : undefined
      const familyPath = familyId
        ? `/security-controls/${familyId}`
        : ctx.familyId
          ? `/security-controls/${ctx.familyId}`
          : undefined
      return [
        home,
        {
          labelKey: 'securityControls',
          fallback: 'Security Controls',
          path: normPath,
        },
        {
          labelKey: 'nav.securityFamilyControls',
          fallback: 'Controls',
          label: ctx.familyLabel || undefined,
          path: familyPath,
        },
        { labelKey: 'nav.analysis', fallback: 'Analysis' },
      ]
    },
  },
  {
    test: /^\/security-control-details\//,
    crumbs: (ctx) => {
      const normPath = ctx.norm ? `/security/${ctx.norm}` : undefined
      const familyPath = ctx.familyId
        ? `/security-controls/${ctx.familyId}`
        : undefined
      return [
        home,
        {
          labelKey: 'securityControls',
          fallback: 'Security Controls',
          path: normPath,
        },
        {
          labelKey: 'nav.securityFamilyControls',
          fallback: 'Controls',
          label: ctx.familyLabel || undefined,
          path: familyPath,
        },
        {
          labelKey: 'nav.securityControlDetail',
          fallback: 'Control details',
          label: ctx.controlLabel || undefined,
        },
      ]
    },
  },
  {
    test: /^\/security-controls\//,
    crumbs: (ctx) => {
      const normPath = ctx.norm ? `/security/${ctx.norm}` : undefined
      return [
        home,
        {
          labelKey: 'securityControls',
          fallback: 'Security Controls',
          path: normPath,
        },
        {
          labelKey: 'nav.securityFamilyControls',
          fallback: 'Controls',
          label: ctx.familyLabel || undefined,
        },
      ]
    },
  },
  {
    test: /^\/security-existing\/|^\/security-subd\/|^\/subdomain\//,
    crumbs: (ctx) => {
      const normPath = ctx.norm ? `/security/${ctx.norm}` : undefined
      const familyPath = ctx.familyId
        ? `/security-controls/${ctx.familyId}`
        : undefined
      return [
        home,
        {
          labelKey: 'securityControls',
          fallback: 'Security Controls',
          path: normPath,
        },
        {
          labelKey: 'nav.securityFamilyControls',
          fallback: 'Controls',
          label: ctx.familyLabel || undefined,
          path: familyPath,
        },
        { labelKey: 'nav.securityRelated', fallback: 'Related' },
      ]
    },
  },
  {
    test: /^\/relatedDataMapped\//,
    crumbs: (ctx, pathname) => {
      const match = pathname.match(/^\/relatedDataMapped\/([^/]+)\/([^/]+)/)
      const source = match?.[1]
      return [
        home,
        {
          labelKey: 'sidebar.securityFramework',
          fallback: 'Framework Dependencies',
          path: '/securityFramework',
        },
        ...(source
          ? [
              {
                labelKey: 'frameworkMappingPage.steps.chooseFamily',
                fallback: 'Choose family',
                path: `/norms/${source}`,
              },
            ]
          : []),
        {
          labelKey: 'frameworkMappingPage.steps.reviewMapping',
          fallback: 'Review mapping',
          label: ctx.controlLabel || undefined,
        },
      ]
    },
  },
  {
    test: /^\/securityControlsToMap\//,
    crumbs: (_ctx, pathname) => {
      const match = pathname.match(/^\/securityControlsToMap\/([^/]+)\/([^/]+)/)
      const source = match?.[1]
      return [
        home,
        {
          labelKey: 'sidebar.securityFramework',
          fallback: 'Framework Dependencies',
          path: '/securityFramework',
        },
        ...(source
          ? [
              {
                labelKey: 'frameworkMappingPage.steps.chooseFamily',
                fallback: 'Choose family',
                path: `/norms/${source}`,
              },
            ]
          : []),
        {
          labelKey: 'frameworkMappingPage.steps.chooseControl',
          fallback: 'Choose control',
        },
      ]
    },
  },
  {
    test: /^\/norms\//,
    crumbs: [
      home,
      {
        labelKey: 'sidebar.securityFramework',
        fallback: 'Framework Dependencies',
        path: '/securityFramework',
      },
      {
        labelKey: 'frameworkMappingPage.steps.chooseFamily',
        fallback: 'Choose family',
      },
    ],
  },
  {
    test: /^\/securityFramework$/,
    crumbs: [
      home,
      {
        labelKey: 'sidebar.securityFramework',
        fallback: 'Framework Dependencies',
      },
    ],
  },
  {
    test: /^\/security\//,
    crumbs: [
      home,
      { labelKey: 'securityControls', fallback: 'Security Controls' },
    ],
  },

  /* ---- Settings / team / license / profile ---- */
  {
    test: /^\/select-norm$/,
    crumbs: [
      // Home has no path during setup — App would bounce unconfigured admins back here
      { labelKey: 'home', fallback: 'Home' },
      {
        labelKey: 'selectNormPage.eyebrow',
        fallback: 'Workspace setup',
      },
    ],
  },
  {
    test: /^\/settings$/,
    crumbs: [
      home,
      { labelKey: 'settings', fallback: 'Settings' },
    ],
  },
  {
    test: /^\/team$/,
    crumbs: [
      home,
      { labelKey: 'team', fallback: 'Team' },
    ],
  },
  {
    test: /^\/license$/,
    crumbs: [
      home,
      { labelKey: 'licensePage.navLabel', fallback: 'License' },
    ],
  },
  {
    test: /^\/profile$/,
    crumbs: [
      home,
      { labelKey: 'profile', fallback: 'Profile' },
    ],
  },

  /* ---- Home / dashboard ---- */
  {
    test: /^\/$/,
    crumbs: [home],
  },
]

export function getNavbarCrumbs(
  pathname: string,
  ctx: NavCrumbContext = {}
): NavCrumb[] {
  const rule = RULES.find((r) => r.test.test(pathname))
  if (!rule) {
    return [{ ...home }]
  }

  const crumbs =
    typeof rule.crumbs === 'function'
      ? rule.crumbs(ctx, pathname)
      : rule.crumbs

  return crumbs.map((crumb, index, arr) =>
    index === arr.length - 1 ? { ...crumb, path: undefined } : crumb
  )
}
